import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { type WithTranslation } from "react-i18next";
import { type ReactNode } from "react";
import { type LineItem } from "../models/CartData";
import { PopperTooltip } from "../../../../common/components/Tooltip";
import { DateFormatType } from "~/common/utils/DateFormatter";
import dayjs from "dayjs";
import { formatShippingOrDeliveryDate, msToMonths } from "~/common/utils/dateUtils";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { isConfiguratorProduct } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

type TFunc = WithTranslation["t"];

export function getPackagingLabelAtLineItem({
  t,
  productDescriptor,
  quantity,
  packSize,
  packCode,
}: {
  t: TFunc;
  productDescriptor: ProductDescriptor;
  quantity?: number;
  packSize: number | undefined;
  packCode: string;
}) {
  const product = productDescriptor.base;
  if (!product.quantityVisible) {
    return t("product.dts");
  }

  if (isProductPMA(productDescriptor) || isConfiguratorProduct(product)) {
    return t("cartPage.standardPackaging");
  }

  if (packCode === undefined) {
    return "-";
  }

  if (packSize === undefined) {
    return "-";
  }

  if (quantity !== undefined) {
    return t("cartPage.table.header.quantity.value", {
      packSize: NF.getDefaultFormatter().format(packSize),
      code: packCode,
      qty: Math.floor(quantity / packSize),
    });
  }

  const label = t("cartPage.table.header.quantity.value", {
    packSize: NF.getDefaultFormatter().format(packSize),
    code: packCode,
    qty: "",
  });
  return label.substring(1);
}

export const getPackagingLabel = ({
  t,
  productDescriptor,
  packSize,
  packCode,
}: {
  t: TFunc;
  productDescriptor: ProductDescriptor;
  packSize: number;
  packCode: string;
}) => {
  const product = productDescriptor.base;
  if (!product.quantityVisible) {
    return t("product.dts");
  }

  if (isProductPMA(productDescriptor) || isConfiguratorProduct(product)) {
    return t("cartPage.standardPackaging");
  }

  return t("addToCartPopup.packSize", {
    amount: NF.getDefaultFormatter().format(packSize),
    code: packCode,
  });
};

export const getEDDLabel = (lineItem: LineItem, t: WithTranslation["t"]): ReactNode => {
  // First priority - display logistical comment
  if (lineItem.logisticalComment) {
    if (lineItem.logisticalComment.length > 25) {
      return (
        <PopperTooltip tooltipContent={<span className={"f-no-wrap"}>{lineItem.logisticalComment}</span>}>
          {lineItem.logisticalComment.substring(0, 20) + "..."}
        </PopperTooltip>
      );
    } else {
      return lineItem.logisticalComment;
    }
  }

  const formatDate = (date: Date | undefined | null) =>
    t("estimatedDeliveryDate.date", { date: formatShippingOrDeliveryDate(date, DateFormatType.DATE) });

  // Second priority - actual EDD as calculated by us
  if (lineItem.edd) {
    if (lineItem.edd.type === "estimate") {
      return formatDate(lineItem.edd.date);
    } else if (lineItem.edd.type === "replenishment") {
      const ms = dayjs(lineItem.edd.date).diff(new Date());
      const months = Math.ceil(msToMonths(ms));
      return t("estimatedDeliveryDate.replenishment", { count: months });
    } else {
      if (import.meta.env.DEV) {
        console.error(`Not implemented case of edd. Type is ${lineItem.edd.type}`);
      }
      return "-";
    }
  }

  // Third priority - ESD which comes from IBIS
  if (lineItem.estimatedShippingDate) {
    return formatDate(lineItem.estimatedShippingDate);
  }

  // Fallback - no value to show means a dash
  return formatDate(null);
};

export function isTariffSurchargeEnabled() {
  return window.SCHA.config.isTariffSurchargeEnabled;
}

export function isMinOrderQuantityRestrictionEnabled() {
  return window.SCHA.config.isMinOrderQuantityRestrictionEnabled;
}
